import { useLastAppointment } from "context/last-appointment";
import useFeatureManager from "hooks/featureManager";
import { isHpv } from "models/appointment";
import { getAppointmentNeedingMissedPayment } from "models/user";
import React, { useEffect, useState } from "react";
import { Loader } from "react-feather";
import {
    Redirect,
    Route,
    Switch,
    useHistory,
    useLocation,
    useParams,
} from "react-router-dom";
import ROUTES from "utils/routes";
import { Footer, Header, Modal } from "../../components";
import useAuthUser from "../../hooks/auth-user";
import useLanguage from "../../hooks/language";
import { ConfirmPhoneNumberModal } from "../../modals";
import MissedAppointmentPaymentModal from "../../modals/missed-appointment-payment";
import AppointmentConfirmation from "./appointment-confirmation";
import styles from "./home.module.css";
import MyAppointments from "./my-appointments";
import Nav from "./nav";
import ScreeningPage from "./screening";
import HpvAppointmentConfirmation from "./screening/hpv-appointment-confirmation";
import ScreeningAppointmentConfirmation from "./screening/screening-appointment-confirmation";

function ScreeningAppointmentConfirmationRoute(): React.ReactElement | null {
    const [authUser] = useAuthUser();
    const { id } = useParams<{ id: string }>();
    const appointment = authUser?.appointments.find(
        (candidate) => String(candidate.id) === id
    );

    if (appointment && isHpv(appointment)) {
        return <HpvAppointmentConfirmation />;
    }

    return <ScreeningAppointmentConfirmation />;
}

export default function HomePage(): React.ReactElement {
    const [authUser, setAuthUser] = useAuthUser();
    const [featureManager] = useFeatureManager();
    const [canLoadPageContent, setCanLoadPageContent] = useState(false);
    const { openPaymentForm } = useLastAppointment();
    const { translations } = useLanguage();
    const { push } = useHistory();

    const location = useLocation();
    const showHeader = !location.pathname.includes("screening");

    useEffect(() => {
        if (!authUser) return;

        if (
            authUser.tfaPreference === "sms" &&
            !authUser.phoneNumberConfirmed
        ) {
            setConfirmationModalVisible(true);
        }
    }, [authUser, featureManager]);

    useEffect(() => {
        setCanLoadPageContent(!!authUser);
    }, [authUser]);

    const [confirmationModalVisible, setConfirmationModalVisible] = useState(
        false
    );

    const onConfirmPhoneNumber = () => {
        setConfirmationModalVisible(false);
        if (authUser) setAuthUser({ ...authUser, phoneNumberConfirmed: true });
    };

    const onScreeningLinkClick = (e: React.MouseEvent) => {
        e.preventDefault();

        if (authUser) {
            const appointmentToPay = getAppointmentNeedingMissedPayment(
                authUser
            );
            if (appointmentToPay) {
                openPaymentForm(appointmentToPay);
                return;
            }
        }

        push(ROUTES.AVAILABILITY);
    };

    return (
        <div className="page">
            <Modal
                title={translations.phoneConfirmationModal.title}
                visible={confirmationModalVisible}
                onVisibilityChange={setConfirmationModalVisible}
            >
                <ConfirmPhoneNumberModal
                    visible={confirmationModalVisible}
                    phoneNumber={authUser ? authUser.phone : ""}
                    onConfirm={onConfirmPhoneNumber}
                    linkToProfile={true}
                />
            </Modal>
            <Header />
            <MissedAppointmentPaymentModal />
            {canLoadPageContent && (
                <div className={styles["header"]}>
                    {showHeader && (
                        <div className={styles["header-content"]}>
                            <div className={styles["header-title"]}>
                                {translations.homePage.title}
                            </div>
                            <Nav onScreeningLinkClick={onScreeningLinkClick} />
                        </div>
                    )}
                </div>
            )}
            {canLoadPageContent && (
                <Switch>
                    <Route
                        exact
                        path={ROUTES.SCREENING_APPOINTMENT_CONFIRMATION()}
                    >
                        <ScreeningAppointmentConfirmationRoute />
                    </Route>
                    <Route exact path={ROUTES.APPOINTMENT_CONFIRMATION()}>
                        <AppointmentConfirmation />
                    </Route>
                    <Route exact path={`${ROUTES.APPOINTMENTS}`}>
                        <MyAppointments
                            tabHeaderClassName={styles["tabs-header"]}
                        />
                    </Route>
                    <Route path={`${ROUTES.SCREENING}`}>
                        <ScreeningPage />
                    </Route>
                    <Redirect to={`${ROUTES.APPOINTMENTS}`} />
                </Switch>
            )}
            {!canLoadPageContent && (
                <div className={styles["loader-container"]}>
                    <div className={styles["loader"]}>
                        <Loader className="spinorama" />
                        <p>{translations.homePage.loadingInfo}</p>
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
}
